import { IPDFGeneratorService } from "../interfaces/IPDFGeneratorService";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import  Constants  from "../constants/Constansts";
import { start } from "repl";

interface LinkRect {
    href: string;
    rects: Array<{ x: number; y: number; width: number; height: number }>;
}

export class PDFGeneratorService implements IPDFGeneratorService {
    async exportHtmlToPDF(elementId: string): Promise<string> {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const fileName = `chat-export-${timestamp}.pdf`;
        const pdf = await this.generatePDF(elementId)
        pdf.save(fileName);
        return fileName;
    }

    async exportHtmlToBase64(elementId: string): Promise<string> {
        const pdf = await this.generatePDF(elementId);
        const base64String = pdf.output('datauristring').split(',')[1];
        return base64String;
    }

    private collectLinkRects(container: HTMLElement): LinkRect[] {
        const containerRect = container.getBoundingClientRect();
        const results: LinkRect[] = [];
        const anchors = container.querySelectorAll('a[href]');

        anchors.forEach(anchor => {
            const href = anchor.getAttribute('href') || '';
            if (!href || href.startsWith('#')) return;

            const clientRects = Array.from(anchor.getClientRects());
            const rects = clientRects.map(r => ({
                x: (r.left - containerRect.left) + container.scrollLeft,
                y: (r.top - containerRect.top) + container.scrollTop,
                width: r.width,
                height: r.height,
            }));

            if (rects.length > 0) {
                results.push({ href, rects });
            }
        });

        return results;
    }

/**
 * Creates a dummy container containing a "chunk" of elements 
 * that stay within the 30,000px height limit.
 */
private createChunkElement(
    sourceElementId: string, 
    startIndex: number = 0, 
    maxHeight: number = 28000 // Safety buffer below 32k
): { dummyRoot: HTMLElement; nextIndex: number } {
    
    const sourceRoot = document.getElementById(sourceElementId);
    if (!sourceRoot) throw new Error("Source element not found");

    const children = Array.from(sourceRoot.getElementsByClassName(Constants.MessageContainerClass)) as HTMLElement[];
    const VIRTUAL_WIDTH = 760;
    
    // 1. Create the Dummy Root and apply original styles
    const dummyRoot = document.createElement('div');
    Object.assign(dummyRoot.style, {
        position: 'fixed',
        top: '0',
        left: '-10000px',
        width: `${VIRTUAL_WIDTH}px`,
        minWidth: `${VIRTUAL_WIDTH}px`,
        backgroundColor: 'white',
        color: 'black',
        boxSizing: 'border-box',
        padding: '20px 30px 20px 20px',
        visibility: 'visible',
        display: 'block',
        overflow: 'visible'
    });

    let currentStackHeight = 0;
    let currentIndex = startIndex;

    // 2. Iterate through children starting from where we last left off
    while (currentIndex < children.length) {
        const child = children[currentIndex];
        const childHeight = child.offsetHeight;

        // If a single element is somehow > 30k, we must allow at least one 
        // to prevent infinite loops, otherwise check against limit.
        if (currentStackHeight + childHeight > maxHeight && dummyRoot.childElementCount > 0) {
            break; 
        }

        // Clone and Append
        const clone = child.cloneNode(true) as HTMLElement;
        dummyRoot.appendChild(clone);
        
        currentStackHeight += childHeight;
        currentIndex++;
    }

    // Attach to body so html2canvas can "see" and measure it
    document.body.appendChild(dummyRoot);

    return {
        dummyRoot,
        nextIndex: currentIndex // Return this so we know where to start the next chunk
    };
}

private async generatePDF(elementId: string): Promise<jsPDF> {
    // const element = document.getElementById(elementId);

    
    // 5. PDF Config
    const pdf = new jsPDF('p', 'mm', 'a4', true);
    const marginMm = 10;
    const descenderBufferMm = 2;
    const pageWidthMm = pdf.internal.pageSize.getWidth();
    const pageHeightMm = pdf.internal.pageSize.getHeight();
    const usableWidthMm = pageWidthMm - (2 * marginMm);
    const usableHeightMm = pageHeightMm - (2 * marginMm) - descenderBufferMm;
    
    
    let startIndex = 0;
    // Track first page usage across all chunks to avoid overwriting the same page
    let hasWrittenFirstPage = false;
    while (true) {
        const { dummyRoot: element, nextIndex } = this.createChunkElement(elementId, startIndex, 28000);

        // Stop if no new content was added
        if (!element || element.childElementCount === 0 || nextIndex === startIndex) {
            if (element && element.parentElement) {
                document.body.removeChild(element);
            }
            break;
        }

        // Advance for next chunk on next loop iteration
        startIndex = nextIndex;

    if (!element) throw new Error("Element not found");

    // 1. CONSTANTS
    const VIRTUAL_WIDTH = 760; 

    // 2. Setup Cloned Element
    const clonedElement = element.cloneNode(true) as HTMLElement;
    Object.assign(clonedElement.style, {
        position: 'fixed',
        top: '0',
        left: '-10000px',
        width: `${VIRTUAL_WIDTH}px`,
        minWidth: `${VIRTUAL_WIDTH}px`,
        maxWidth: 'none',
        height: 'auto',
        margin: '0',
        // FIX 1: Use border-box and generous padding to push content away from edges
        boxSizing: 'border-box',
        padding: '20px 30px 20px 20px', // Extra 10px padding on Right (30px)
        background: 'white',
        color: 'black',
        visibility: 'visible',
        overflow: 'visible', // Ensure nothing hidden
        fontSize: '14px',
        lineHeight: '1.5'
    });

    document.body.appendChild(clonedElement);

    // FIX 2: Recalculate width to include the new padding
    const naturalWidth = clonedElement.scrollWidth;
    // Add a tiny +5px safety buffer to the capture width
    const finalWidth = Math.max(naturalWidth, VIRTUAL_WIDTH) + 5; 
    
    clonedElement.style.width = `${finalWidth}px`;

    await new Promise(resolve => setTimeout(resolve, 500));

    const fullHeight = clonedElement.offsetHeight;

    // 3. Capture Canvas
    const fullCanvas = await html2canvas(clonedElement, {
        useCORS: true,
        scale: 2, 
        backgroundColor: '#ffffff',
        width: finalWidth, // Capture slightly wider than content
        height: fullHeight,
        windowWidth: finalWidth,
        scrollX: 0, // Force top-left alignment
        scrollY: 0,
        logging: false
    });

    const elementRect = clonedElement.getBoundingClientRect();
    const scaleY = fullCanvas.height / elementRect.height;
    
    // 4. Tag-Aware Boundaries
    const allTags = Array.from(clonedElement.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, pre, img, blockquote, table, .reference-link'));
    const boundaries = allTags.map(el => {
        const r = el.getBoundingClientRect();
        return {
            top: (r.top - elementRect.top) * scaleY,
            bottom: (r.bottom - elementRect.top) * scaleY
        };
    }).sort((a, b) => a.top - b.top);

    const linkRects = Array.from(clonedElement.querySelectorAll('a[href]')).map(anchor => {
        const r = anchor.getBoundingClientRect();
        return {
            href: anchor.getAttribute('href') || '',
            x: (r.left - elementRect.left) * (fullCanvas.width / finalWidth),
            y: (r.top - elementRect.top) * scaleY,
            w: r.width * (fullCanvas.width / finalWidth),
            h: r.height * scaleY
        };
    });

    document.body.removeChild(clonedElement);
    // Clean up the off-screen dummy root created for this chunk
    if (element && element.parentElement) {
        document.body.removeChild(element);
    }

    const pxToMm = usableWidthMm / fullCanvas.width;
    const pageHeightPx = usableHeightMm / pxToMm;
    // Overflow logic from previous step
    const maxOverflowPx = (8 / pxToMm); 

    let currentY = 0;
    const endThreshold = 2; 

    // 6. Rendering Loop
    while (currentY < fullCanvas.height - endThreshold) {
        // Add a new page for every slice except the very first one across all chunks
        if (!hasWrittenFirstPage) {
            hasWrittenFirstPage = true;
        } else {
            pdf.addPage();
        }

        let targetEndY = currentY + pageHeightPx;
        const totalRemainingPx = fullCanvas.height - currentY;

        if (totalRemainingPx <= pageHeightPx) {
            targetEndY = fullCanvas.height;
        } 
        else if (totalRemainingPx <= (pageHeightPx + maxOverflowPx)) {
            targetEndY = fullCanvas.height;
        }
        else {
            const fittingBoundaries = boundaries.filter(b => b.top >= currentY && b.bottom <= (currentY + pageHeightPx));
            if (fittingBoundaries.length > 0) {
                targetEndY = fittingBoundaries[fittingBoundaries.length - 1].bottom;
            }
        }

        if (targetEndY <= currentY) targetEndY = Math.min(fullCanvas.height, currentY + pageHeightPx);

        const sliceHeight = targetEndY - currentY;
        
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = fullCanvas.width;
        sliceCanvas.height = sliceHeight;
        
        const ctx = sliceCanvas.getContext('2d');
        if (ctx) {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
            ctx.drawImage(fullCanvas, 0, currentY, fullCanvas.width, sliceHeight, 0, 0, sliceCanvas.width, sliceHeight);
        }

        const imgData = sliceCanvas.toDataURL('image/jpeg', 0.8);
        pdf.addImage(imgData, 'JPEG', marginMm, marginMm, usableWidthMm, sliceHeight * pxToMm, undefined, 'FAST');

        linkRects.forEach(link => {
            if (link.y >= currentY && link.y < targetEndY) {
                pdf.link(
                    (link.x * pxToMm) + marginMm,
                    ((link.y - currentY) * pxToMm) + marginMm,
                    link.w * pxToMm,
                    link.h * pxToMm,
                    { url: link.href }
                );
            }
        });

        currentY = targetEndY;
    }
}

    return pdf;
}

}

export default PDFGeneratorService;